import { useScrollAnimation } from "../../../hooks/useScrollAnimation";
import { testimonials } from "../../../mocks/products";

function TestimonialCard({
  testimonial,
  delay,
}: {
  testimonial: (typeof testimonials)[0];
  delay: number;
}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className="relative px-8 py-10 transition-all duration-1000"
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
      }}
    >
      {/* Decorative quote mark */}
      <div
        className="text-[#B76E79]/20 text-[100px] leading-none absolute -top-4 left-4 select-none"
        style={{ fontFamily: "Playfair Display, serif" }}
      >
        &ldquo;
      </div>

      <p
        className="text-[#6B5D4F] text-lg leading-relaxed italic mb-8 relative z-10"
        style={{ fontFamily: "Playfair Display, serif", fontWeight: 400 }}
      >
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      <div className="flex items-center gap-4">
        <div className="w-8 h-px bg-[#B76E79]/50" />
        <div>
          <p
            className="text-[#6B4423] text-sm font-medium"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {testimonial.author}
          </p>
          <p
            className="text-[#7A6F5D]/70 text-xs mt-0.5"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
          >
            {testimonial.title}
          </p>
        </div>
      </div>

      {/* Star row */}
      <div className="flex items-center gap-1 mt-5">
        {[...Array(5)].map((_, i) => (
          <i key={i} className="ri-star-fill text-[#D4AF87] text-xs" />
        ))}
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#F5F1EA] py-20 md:py-40">
      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 md:px-10">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center mb-12 md:mb-20 transition-all duration-1000 ${
            headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p
            className="text-[#B76E79] text-[11px] uppercase tracking-[4px] mb-4 sm:mb-5"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            What They Say
          </p>
          <h2
            className="text-[#6B4423] text-4xl sm:text-5xl"
            style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
          >
            Worn With Love
          </h2>
          <div className="w-16 h-px bg-[#B76E79]/40 mx-auto mt-6 sm:mt-8" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {testimonials.map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} delay={i * 200} />
          ))}
        </div>
      </div>
    </section>
  );
}
